import { styled, useTheme } from 'styled-components'
import { SearchBrandScheme } from '@/apis/rest/generated/types'
import { ProductsListItemResponseDto } from '@/apis/rest/generated/types/productsListItemResponseDto'
import { Typo, TypoVariant } from '@/components/common/typography'
import { ScrollContainer } from '@/components/domains/etc'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import { ProductLocation } from '@/constants/product-location.const'
import { useSearchEventTracker } from '@/containers/event-tracker/search.event-tracker'
import { AdPetType } from '@/graphql/generated/schemas'
import { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import { removeScrollbar } from '@/utils/utilCSS'

type SearchResultBrandProductsListProps = {
  products: ProductsListItemResponseDto[]
  brands: SearchBrandScheme[]
  searchKeyword: string
  petType: AdPetType
  searchLocation: string
}

export const SearchResultBrandProductsList = ({
  products,
  brands,
  searchKeyword,
  petType,
  searchLocation,
}: SearchResultBrandProductsListProps) => {
  const { color } = useTheme()

  const { trackClickSearchResultBrandProduct } = useSearchEventTracker()

  const handleClickProductCard = (product: ProductTypeDefinedByFrontend) => {
    trackClickSearchResultBrandProduct({
      searchKeyword,
      petType,
      searchLocation,
      brand: brands[0],
      product,
    })
  }

  return (
    <StyledSearchResultBrandProductsListWrapper className="brand-products__wrapper">
      <div className="brand-products__title">
        <Typo as="h2" variant={TypoVariant.Heading6Bold} color={color.gray900}>
          내가 찾아 본<br />
          <div className="brand-products__keyword-recommendation">
            <Typo
              as="span"
              variant={TypoVariant.Heading6Bold}
              color={color.blue500}
              style={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                maxWidth: '18rem',
                whiteSpace: 'nowrap',
                marginRight: '0.4rem',
              }}
            >
              {searchKeyword}
            </Typo>
            관련 추천상품
          </div>
        </Typo>
      </div>
      <ScrollContainer>
        <section className="brand-products__list">
          {products?.map((product, index) => {
            const productData = transformProductTypeDefinedByFrontend(product)

            return (
              <div className="brand-products__item" key={product.id}>
                <NewProductCard
                  hasCartButton
                  trackerData={{
                    addLocation: ProductLocation.SearchResultBrand,
                    searchKeyword,
                    searchLocation,
                    listIndex: index,
                    listType: 'Brand',
                  }}
                  product={productData}
                  size={PRODUCT_CARD_SIZES.lg}
                  onClickProductCard={() => handleClickProductCard(productData)}
                />
              </div>
            )
          })}
        </section>
      </ScrollContainer>
    </StyledSearchResultBrandProductsListWrapper>
  )
}

const StyledSearchResultBrandProductsListWrapper = styled.div`
  padding-bottom: 2.4rem;
  .brand-products {
    &__title {
      padding-left: 1.6rem;
      margin-bottom: 0.8rem;
    }

    &__keyword-recommendation {
      display: flex;
      align-items: center;
    }

    &__list {
      ${removeScrollbar};
      display: flex;
      gap: 0.8rem;

      & > *:first-child {
        margin-left: 16px;
      }

      & > *:last-child {
        padding-right: 16px;
      }
    }
  }
`
